import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Label,
  Button,
  Form,
  FormGroup,
  Card,
} from "reactstrap";
import {
  addOrderAction,
  getPaymentMethodAction,
  addCardAction,
} from "../../store/actions/orderAction.js";
import {
  clearCartAction,
  updateTableData,
} from "../../store/actions/cartAction.js";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSingleUser } from "../../store/actions/authAction.js";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";

function StripePayment({ amount, orderData, forwardFees, stripeClientKey }) {
  const dispatch = useDispatch();
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const { darkMode } = useSelector((state) => state.global);
  let restaurantID = localStorage.getItem("restaurantId");
  let { user, uid } = useSelector((state) => state.authUser);
  let stripePromise = loadStripe(stripeClientKey);

  function calculateFinalAmount(amount, forwardFees) {
    if (!forwardFees) {
      return amount;
    }

    if (forwardFees.type === "fixed_price") {
      return amount + parseFloat(forwardFees.fixedAmount);
    } else if (forwardFees.type === "percentage") {
      const percentage = parseFloat(forwardFees.percentageAmount) / 100;
      return amount + amount * percentage;
    } else if (forwardFees.type === "both") {
      const percentage = parseFloat(forwardFees.percentageAmount) / 100;
      return amount + parseFloat(forwardFees.fixedAmount) + amount * percentage;
    }

    return amount;
  }

  const finalAmount = useMemo(
    () => calculateFinalAmount(amount, forwardFees),
    [amount, forwardFees]
  );

  let taxAmount = "";

  if (forwardFees?.type === "fixed_price") {
    taxAmount = forwardFees.fixedAmount;
  } else if (forwardFees?.type === "percentage") {
    taxAmount = parseFloat(forwardFees.percentageAmount);
  } else if (forwardFees?.type === "both") {
    taxAmount = `${parseFloat(forwardFees.percentageAmount)}% + ${
      forwardFees.fixedAmount
    }`;
  }

  const { singleUser } = useSelector((state) => state.user);
  useEffect(() => {
    dispatch(getSingleUser(uid));
    dispatch(getPaymentMethodAction(uid));
  }, [dispatch, uid]);
  const history = useHistory();
  const [paymentLoader, setPaymentLoader] = useState(false);

  const handleSubmit = async (e, elements, stripe) => {
    e.preventDefault();
    setPaymentLoader(true);

    if (!stripe || !elements) {
      setPaymentLoader(false);
      return;
    }

    try {
      const cardElement = elements.getElement(CardNumberElement);
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        setPaymentLoader(false);
        throw new Error(error.message);
      }

      const body = {
        tokenId: token.id,
        restaurantID,
        amount: Number(finalAmount),
        email: user?.email,
        currency: "USD",
      };

      const res = await axios.post(
        "https://us-central1-menux1.cloudfunctions.net/app/payment",
        body
      );

      const { data } = res;
      dispatch(addCardAction(body, uid));

      const obj = {
        ...orderData,
        customerID: uid,
        totalAmount: finalAmount || 0,
        paymentMethod: "Stripe",
        paymentRecieved: true,
        status: "pending",
        customerName: singleUser?.name,
        paymentID: data?.charge?.id,
      };

      dispatch(
        addOrderAction(obj, (orderID) => {
          history.push(`/tracking/orderID=${orderID}`);
          dispatch(clearCartAction());
          localStorage.removeItem("cartPath");
        })
      );
      if (obj.tableNumber) {
        dispatch(
          updateTableData(
            obj.restaurantID,
            uid,
            obj.tableNumber,
            obj.timer,
            restaurantsDetail?.name
          )
        );
      }
    } catch (err) {
      toast.error(err?.message || "Payment failed");
      console.error(err?.response?.data?.message || err.message, "Error");

      if (elements) {
        elements.getElement(CardNumberElement)?.clear();
        elements.getElement(CardExpiryElement)?.clear();
        elements.getElement(CardCvcElement)?.clear();
      }
    } finally {
      setPaymentLoader(false);
    }
  };

  return (
    <Card
      className={
        darkMode
          ? "px-3 mb-3 cart__payment_card-payment-dark "
          : "px-3 mb-3 cart__payment_card-payment "
      }
    >
      <Elements stripe={stripePromise}>
        <ElementsConsumer>
          {({ elements, stripe }) => (
            <Form onSubmit={(e) => handleSubmit(e, elements, stripe)}>
              <FormGroup>
                <Label for="cardNumber">Card Number:</Label>
                <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                  <div
                    style={{
                      flexBasis: "96%",
                    }}
                  >
                    <CardNumberElement
                      required
                      options={{
                        placeholder: "XXXX XXXX XXXX XXXX",
                        style: {
                          base: {
                            // backgroundColor: "#232733",
                            fontSize: "16px",
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup>
                <Label for="expiryDate">Expiry Date:</Label>
                <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                  <div
                    style={{
                      flexBasis: "96%",
                    }}
                  >
                    <CardExpiryElement
                      required
                      options={{
                        placeholder: "MM/YY",
                        style: {
                          base: {
                            fontSize: "16px",
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup>
                <Label for="cvv">CVC/CVV:</Label>
                <div className="form-control mt-2 d-flex justify-content-between align-items-center shadow-sm">
                  <div
                    style={{
                      flexBasis: "96%",
                    }}
                  >
                    <CardCvcElement
                      required
                      options={{
                        placeholder: "...",
                        style: {
                          base: {
                            fontSize: "16px",
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </FormGroup>
              {taxAmount && taxAmount > 0 && (
                <div className="d-flex justify-content-center my-2">
                  <span className="fs--22 fw--400">
                    {forwardFees?.type == "percentage"
                      ? "%"
                      : orderData?.order[0]?.currency}
                  </span>
                  <span className="fs--22 fw--400 mr-1">
                    Tax Fees: {}
                    {taxAmount || 0}
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-center my-1">
                <span className="fs--22 fw--400 mr-1">
                  Total Amount: {finalAmount ? finalAmount?.toFixed(2) : 0}
                </span>
                <span className="fs--22 fw--400">
                  {orderData?.order[0]?.currency}
                </span>
              </div>
              <Button
                className="auth-button mt-2 py-3 btn btn-lg btn-block border border-none"
                type="submit"
                style={{
                  backgroundColor: "#f2ba36",
                }}
                disabled={paymentLoader}
              >
                <h4 className="mb-0">Pay with Stripe</h4>
              </Button>
            </Form>
          )}
        </ElementsConsumer>
      </Elements>
    </Card>
  );
}

export default StripePayment;
